import Components from "../components";

class Sticky extends Components.Base {
  initialize() {
    this.stickyEl = this.$el[0];
    this.updateScrollPadding();
    this.maxScrollY = document.body.offsetHeight - window.innerHeight;
  }

  // Add scroll offset (subnav height) to the page
  updateScrollPadding() {
    // The bottom border is on .c-subnav, inside this element, so clientHeight
    // already counts it.
    this.scrollPaddingTop = this.stickyEl.clientHeight;
    document.documentElement.style.scrollPaddingTop = `${this.scrollPaddingTop}px`;
  }

  bind() {
    // Observe the sticky nav and add .is-sticky class
    this.stickyObserver = new IntersectionObserver(
      ([e]) => e.target.classList.toggle("is-sticky", e.intersectionRatio < 1),
      { threshold: [1] },
    );
    this.stickyObserver.observe(this.stickyEl);

    // The bar's height settles after load, and changes with the viewport,
    // which can also push the active nav pill out of its row
    this.resizeObserver = new ResizeObserver(() => {
      this.updateScrollPadding();
      const active = this.stickyEl.querySelector(".c-subnav .nav-link.active");
      if (active) this.scrollNavLinkIntoView(active);
    });
    this.resizeObserver.observe(this.stickyEl);

    // Custom scrollspy implementation
    const targets = Array.from(document.querySelectorAll(".scrollspy-target"));

    this.handleNavLinksClick();
    this.spyTargets(targets);

    this.abortController = new AbortController();
    document.addEventListener(
      "scroll",
      () => {
        this.maxScrollY = document.body.offsetHeight - window.innerHeight;
        this.spyTargets(targets);
      },
      { signal: this.abortController.signal },
    );
  }

  unbind() {
    this.stickyObserver.unobserve(this.$el[0]);
    this.resizeObserver.disconnect();
    this.abortController.abort();
  }

  spyTargets(targets) {
    const navLinks = this.getNavLinks();

    targets.forEach((targetEl) => {
      const hash = `#${targetEl.id}`;
      const navLink = navLinks.find((el) => el.hash === hash);
      const [topOffset, bottomOffset] = this.getElementOffsets(targetEl);
      const a = targetEl.offsetTop - this.scrollPaddingTop - topOffset - 1; // bottom border of 1px
      const b =
        a + targetEl.offsetHeight + this.scrollPaddingTop - bottomOffset;

      if (!navLink) return;

      if (window.scrollY >= a && window.scrollY < b) {
        this.activateNavLink(navLinks, navLink, targetEl);
      } else {
        navLink.classList.remove("active");
      }
    });

    // Activate the first nav link if the page is scrolled before the first section/target
    if (window.scrollY <= targets[0].offsetTop) {
      this.activateNavLink(navLinks, navLinks[0], targets[0]);
    }

    // Activate the last nav link if the page scroll reaches the bottom.
    // offsetHeight is rounded and Firefox's scrollY isn't.
    if (this.maxScrollY - window.scrollY < 1) {
      this.activateNavLink(
        navLinks,
        navLinks[navLinks.length - 1],
        targets[targets.length - 1],
      );
    }
  }

  getNavLinks() {
    return Array.from(this.stickyEl.querySelectorAll(".c-subnav .nav-link"));
  }

  handleNavLinksClick() {
    const navLinks = this.getNavLinks();

    navLinks.forEach((navLink) => {
      navLink.addEventListener("click", (e) => {
        e.preventDefault();

        const targetId = e.currentTarget.hash.split("#")[1];
        const targetEl = document.getElementById(targetId);
        targetEl.scrollIntoView();

        window.location.hash = targetId;
      });
    });
  }

  getElementOffsets(el) {
    const cs = getComputedStyle(el);
    const topOffset =
      parseInt(cs.getPropertyValue("margin-top")) +
      parseInt(cs.getPropertyValue("padding-top"));
    const bottomOffset =
      parseInt(cs.getPropertyValue("margin-bottom")) +
      parseInt(cs.getPropertyValue("padding-bottom"));

    return [topOffset, bottomOffset];
  }

  activateNavLink(navLinks, activatedNavLink, targetEl) {
    const hash = `#${targetEl.id}`;

    if (!activatedNavLink.classList.contains("active")) {
      this.scrollNavLinkIntoView(activatedNavLink);
    }

    navLinks.forEach((el) => el.classList.remove("active"));
    activatedNavLink.classList.add("active");
    this.updateHREFs(hash);
  }

  // On phones the nav pills are one row that scrolls sideways (see
  // _sticky.scss); center the active one in it.
  scrollNavLinkIntoView(navLink) {
    const nav = navLink.closest(".nav");
    if (nav.scrollWidth <= nav.clientWidth) return;

    const navRect = nav.getBoundingClientRect();
    const linkRect = navLink.getBoundingClientRect();
    nav.scrollLeft +=
      linkRect.left - navRect.left - (navRect.width - linkRect.width) / 2;
  }

  updateHREFs(hash) {
    Array.from(this.$el.find(".c-notice-pager a")).forEach((link) =>
      link.setAttribute("href", link.href.split("#")[0] + hash),
    );
  }
}

Components.collection.register({ selector: ".c-sticky", klass: Sticky });
